import React, { useMemo, useState } from "react";
import {
    Autocomplete,
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    IconButton,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Switch,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import {
    useForm,
    useFormActions,
    useMeta,
    useNotification,
    useValidator,
} from "@/hooks";
import {
    MUTATION_UPDATE_STICKY,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { useStoreContext } from "context";
import { RowActionsMenu, storeTableUiStyles } from "./shared";
import styles from "./GeneralsForm.module.scss";
import stickyStyles from "./StickyForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const CopyIcon = () => <Icon icon="copy" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const StickyForm = ({ app }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const { providers } = useStoreContext();
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [walletIds, setwalletIds] = useState([]);
    const [rowSelect, setRowSelect] = useState(null);
    const [newWalletUrl, setNewWalletUrl] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { inputs, setInputs, inputChange } = useForm();

    const {
        inputs: inputsUrl,
        setInputs: setInputsUrl,
        inputChange: inputChangeUrl,
    } = useForm({ wallet_url: "" });

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "api_key",
                method: "isEmpty",
                validWhen: false,
                message: "API Key is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const {
        validator: validatorUrl,
        isValid: isValidUrl,
        isSubmited: isSubmitedUrl,
    } = useValidator(
        [
            {
                field: "wallet_url",
                method: "isEmpty",
                validWhen: false,
                message: "Wallet url is required.",
            },
        ],
        { ...inputsUrl },
    );

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const formatWalletUrls = walletUrls => {
        if (!walletUrls) {
            return [];
        }
        return walletUrls?.map(c => ({
            provider_store_id: c?.sticky_provider_store_id,
            url: c?.sticky_url,
        }));
    };

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: _.omit(
                {
                    ...inputs,
                    wallet_urls: formatWalletUrls(inputs.wallet_urls ?? []),
                },
                ["__typename"],
            ),
        },
    };

    const [mutate] = useMutation(MUTATION_UPDATE_STICKY, {
        refetchQueries: [],
        onCompleted: resp => {
            const normalized = (newWalletUrl || [])?.map(w => ({
                sticky_provider_store_id: w.sticky_provider_store_id,
                sticky_url: w.sticky_url,
            }));
            setInputs(prev => ({
                ...prev,
                wallet_urls: normalized,
            }));

            handleCancel();
            notify(
                `Wallet was ${!isDelete ? "Updated" : "Removed"} Successfully`,
                "success",
            );
        },
        onError: () => {
            handleCancel();
            notify(
                `Error ${
                    !isDelete ? "Updating" : "Removing"
                } The Wallet, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const confirmDelete = () => {
        const baseUrls = inputs.wallet_urls || [];
        const filteredWalletUrl = baseUrls.filter(
            (i, index) => index !== rowSelect,
        );
        setNewWalletUrl(filteredWalletUrl);
        setShowDeleteConfirm(false);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: _.omit(
                    {
                        ...inputs,
                        wallet_urls: formatWalletUrls(filteredWalletUrl),
                    },
                    ["__typename"],
                ),
            },
        });
    };

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Sticky",
        "",
        MUTATION_UPDATE_STICKY,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const getStoreName = providerStoreId => {
        const store = providers.find(s => s.value === providerStoreId);
        return store ? store.label : providerStoreId || "";
    };

    const _walletUrlsForTable = inputs?.wallet_urls || [];

    const rows = useMemo(
        () =>
            _walletUrlsForTable.map((wallet, index) => ({
                ...wallet,
                _index: index,
            })),
        [_walletUrlsForTable],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating}
                            label={
                                getStoreName(row.sticky_provider_store_id) ||
                                `wallet ${row._index + 1}`
                            }
                            onDelete={() => {
                                setIsDelete(true);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                const rowData = _walletUrlsForTable[row._index];
                                if (!rowData) return;
                                setwalletIds([
                                    rowData.sticky_provider_store_id,
                                ]);
                                setInputsUrl({
                                    wallet_url: rowData.sticky_url || "",
                                });
                            }}
                            updateLabel="Edit"
                        />
                    </span>
                ),
            },
            {
                id: "store",
                header: "Store",
                minWidth: 200,
                renderCell: row =>
                    getStoreName(row.sticky_provider_store_id) || "",
            },
            {
                id: "wallet_url",
                header: "Wallet URL",
                minWidth: 280,
                renderCell: row =>
                    row.sticky_url ? (
                        <Badge
                            className={stickyStyles.walletBadge}
                            variant="neutral"
                        >
                            <a
                                className={stickyStyles.walletLink}
                                href={row.sticky_url}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {row.sticky_url}
                            </a>
                        </Badge>
                    ) : (
                        ""
                    ),
            },
        ],
        [isUpdating, _walletUrlsForTable, providers],
    );

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);
        setNewWalletUrl(array);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...{
                        ...inputs,
                        wallet_urls: formatWalletUrls(array),
                    },
                },
            },
        });
    };

    const configuredStoreIds = useMemo(() => {
        const urls = (inputs && inputs.wallet_urls) || [];
        return urls
            .map(w => w.sticky_provider_store_id ?? w.provider_store_id)
            .filter(Boolean)
            .map(id => String(id));
    }, [inputs?.wallet_urls]);

    const isStoreConfigured = storeValue =>
        configuredStoreIds.includes(String(storeValue));

    const storeOptions = useMemo(
        () =>
            (providers || []).map(store => ({
                value: String(store.value),
                label: store.label,
                iconLeft: <i aria-hidden className="fas fa-store" />,
                trailing: isStoreConfigured(store.value) ? (
                    <span className={stickyStyles.configuredBadge}>
                        Configured
                    </span>
                ) : undefined,
            })),
        [providers, configuredStoreIds],
    );

    const storePickerValues = useMemo(
        () => (walletIds ?? []).map(String),
        [walletIds],
    );

    const onStorePickerChange = selectedIds => {
        const ids = (selectedIds ?? []).map(sid => {
            const store = providers?.find(s => String(s.value) === sid);
            return store ? store.value : sid;
        });
        setwalletIds(ids);
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setIsDelete(false);
        setInputsUrl({
            wallet_url: "",
        });
        setwalletIds([]);
    };

    const selectedStoreNamesForForm =
        walletIds.length > 0
            ? walletIds
                  .map(id => getStoreName(id))
                  .filter(Boolean)
                  .join(", ")
            : "";

    const handleWalletFormSave = () => {
        if (isSubmitedUrl) isSubmitedUrl(true);
        if (!isValidUrl()) return;

        if (!walletIds.length) return;

        const formUrl = (inputsUrl?.wallet_url ?? "").trim();
        if (!formUrl) return;

        const existing = inputs.wallet_urls ?? [];

        const getId = x => x.sticky_provider_store_id;
        const getUrl = x => x.sticky_url;

        const toEntryExisting = x => {
            const id = getId(x);
            const url = (getUrl(x) ?? "").trim();
            return id == null || !url
                ? null
                : { sticky_provider_store_id: id, sticky_url: url };
        };

        const dictExisting = _.mapKeys(
            existing.map(toEntryExisting).filter(Boolean),
            x => String(x.sticky_provider_store_id),
        );
        const dictSelected = _.mapKeys(
            walletIds
                .filter(id => id != null)
                .map(id => ({
                    sticky_provider_store_id: id,
                    sticky_url: formUrl,
                })),
            x => String(x.sticky_provider_store_id),
        );

        handlerUpdatingOrCreate(
            Object.values(_.merge({}, dictExisting, dictSelected)),
        );
        handleCancel();
    };

    const copyApiKey = () => {
        navigator.clipboard.writeText(inputs.api_key || "");
        notify("API Key copied to clipboard", "success");
    };

    return (
        <>
            <Modal
                description="Are you sure you want to remove this wallet?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove wallet"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Treez Loyalty</Heading>
                            <Text tone="secondary" variant="body-sm">
                                Treez Loyalty Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: loading ? undefined : <SaveIcon />,
                                    loading,
                                    disabled: loading,
                                    onClick: () => createOrUpdate(),
                                },
                            ]}
                            aria-label="Treez Loyalty actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.used_for_subscription)}
                                label="Used for subscription"
                                name="used_for_subscription"
                                onChange={inputChange}
                            />
                            <Switch
                                checked={Boolean(inputs.used_for_rewards)}
                                label="Used for rewards"
                                name="used_for_rewards"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={1} maxWidth="twoThirds">
                                <Input
                                    error={fieldError(validator, "api_key")}
                                    fullWidth
                                    iconRight={
                                        <IconButton
                                            aria-label="Copy API Key"
                                            className={stickyStyles.copyButton}
                                            onClick={copyApiKey}
                                            size="sm"
                                            type="button"
                                            variant="tertiary"
                                        >
                                            <CopyIcon />
                                        </IconButton>
                                    }
                                    label="API Key"
                                    name="api_key"
                                    onChange={inputChange}
                                    value={inputs.api_key || ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Store Wallet Configuration">
                            <Text tone="secondary" variant="body-sm">
                                Configure a unique wallet URL for each store
                                provider
                            </Text>
                            <FormLayout.Grid columns={1} maxWidth="twoThirds">
                                <Autocomplete
                                    fullWidth
                                    label="Select store"
                                    multiple
                                    name="store_picker"
                                    onChange={onStorePickerChange}
                                    options={storeOptions}
                                    placeholder="Choose a store to configure..."
                                    value={storePickerValues}
                                />
                            </FormLayout.Grid>

                            {walletIds.length > 0 ? (
                                <div className={stickyStyles.walletForm}>
                                    <div
                                        className={
                                            stickyStyles.walletFormHeader
                                        }
                                    >
                                        <i
                                            aria-hidden
                                            className={`fas fa-wallet ${stickyStyles.walletFormIcon}`}
                                        />
                                        <Text variant="body-sm">
                                            Configure wallet URL for{" "}
                                            <strong>
                                                {selectedStoreNamesForForm ||
                                                    "—"}
                                            </strong>
                                        </Text>
                                    </div>
                                    <div
                                        className={stickyStyles.walletFormBody}
                                    >
                                        <Input
                                            className={
                                                stickyStyles.walletUrlInput
                                            }
                                            error={fieldError(
                                                validatorUrl,
                                                "wallet_url",
                                            )}
                                            fullWidth
                                            label="Wallet URL"
                                            name="wallet_url"
                                            onChange={inputChangeUrl}
                                            placeholder="https://app.stickycards.co/getpass/..."
                                            value={inputsUrl?.wallet_url ?? ""}
                                        />
                                        <Button
                                            className={
                                                stickyStyles.walletSaveButton
                                            }
                                            disabled={
                                                isUpdating ||
                                                !inputsUrl?.wallet_url
                                            }
                                            iconLeft={
                                                isUpdating ? undefined : (
                                                    <SaveIcon />
                                                )
                                            }
                                            loading={isUpdating}
                                            onClick={handleWalletFormSave}
                                            size="lg"
                                            type="button"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </FormLayout.Section>

                        <FormLayout.Section title="Configured Stores">
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default StickyForm;
